import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { LogOut, Upload, Download, Plus, Trash2, Ship, XCircle } from "lucide-react";
import { api, partnerApi, apiError, prettyDate, parsePassengerCsv, downloadPassengerTemplate } from "@/lib/api";

const inputCls = "border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";

const PartnerLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== "partner") throw new Error("This login is for charter partners only");
      localStorage.setItem("mmc_partner_token", data.access_token);
      onLogin();
    } catch (err) {
      toast.error(err?.response ? apiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#071426] pt-28 pb-20 px-4 flex items-start justify-center">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md mt-10">
        <div className="flex items-center gap-2 mb-1">
          <Ship size={22} className="text-sky-600" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Partner Portal</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">Private charter access for partners</p>
        <input data-testid="partner-email-input" type="email" className={`w-full mb-3 ${inputCls}`} placeholder="Partner email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="partner-password-input" type="password" className={`w-full mb-5 ${inputCls}`} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="partner-login-button" type="submit" disabled={busy} className="btn-ocean w-full text-white font-bold py-3 rounded-full disabled:opacity-60">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const PassengerEditor = ({ passengers, setPassengers, testPrefix }) => {
  const fileRef = useRef(null);
  const importCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parsePassengerCsv(String(reader.result));
      if (parsed.length === 0) return toast.error("No passengers found in file. Use the template: Name,Age");
      setPassengers(parsed);
      toast.success(`Imported ${parsed.length} passengers`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <button data-testid={`${testPrefix}-import-csv-button`} onClick={() => fileRef.current?.click()} className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors duration-150">
          <Upload size={15} /> Import CSV
        </button>
        <button data-testid={`${testPrefix}-download-template-button`} onClick={downloadPassengerTemplate} className="flex items-center gap-2 border border-slate-300 text-slate-700 text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors duration-150">
          <Download size={15} /> Download Template
        </button>
        <button data-testid={`${testPrefix}-add-passenger-button`} onClick={() => setPassengers([...passengers, { name: "", age: "" }])} className="flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:text-sky-700 px-2">
          <Plus size={15} /> Add row
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid={`${testPrefix}-csv-file-input`} />
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {passengers.map((p, i) => (
          <div key={i} className="flex gap-2 items-center">
            <span className="text-xs font-bold text-slate-400 w-6">{i + 1}.</span>
            <input data-testid={`${testPrefix}-name-${i}`} className={`flex-1 min-w-0 ${inputCls}`} placeholder="Passenger name" value={p.name}
              onChange={(e) => setPassengers(passengers.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))} />
            <input data-testid={`${testPrefix}-age-${i}`} type="number" min="0" className={`w-16 sm:w-20 shrink-0 ${inputCls}`} placeholder="Age" value={p.age}
              onChange={(e) => setPassengers(passengers.map((x, idx) => (idx === i ? { ...x, age: e.target.value } : x)))} />
            <button onClick={() => setPassengers(passengers.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 transition-colors duration-150">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {passengers.length === 0 && <p className="text-sm text-slate-400">Optional — import a CSV or add passenger names manually.</p>}
      </div>
    </div>
  );
};

const validatePassengers = (passengers) => {
  for (const p of passengers) {
    if (!p.name.trim() || p.age === "" || isNaN(parseInt(p.age, 10))) return "Every listed passenger needs a name and a valid age";
  }
  return null;
};

export default function PartnerPage() {
  const [authed, setAuthed] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ date: "", time: "10:00 AM – 12:00 PM", notes: "", passenger_count: "" });
  const [passengers, setPassengers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editPassengers, setEditPassengers] = useState([]);
  const [editCount, setEditCount] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await partnerApi().get("/partner/bookings");
      setBookings(data);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("mmc_partner_token");
        setAuthed(false);
      } else toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("mmc_partner_token")) load();
    else setAuthed(false);
  }, [load]);

  const submit = async () => {
    if (!form.date) return toast.error("Pick a charter date");
    const count = parseInt(form.passenger_count, 10);
    if (isNaN(count) || count < 1 || count > 50) return toast.error("Enter the number of passengers (1–50)");
    if (passengers.length > count) return toast.error("The passenger list has more names than the passenger count");
    const err = validatePassengers(passengers);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data } = await partnerApi().post("/partner/bookings", {
        date: form.date,
        time: form.time,
        notes: form.notes,
        passenger_count: count,
        passengers: passengers.map((p) => ({ name: p.name.trim(), age: parseInt(p.age, 10) })),
      });
      toast.success(`Charter booked — confirmation ${data.code}. The date is now reserved.`);
      setForm({ date: "", time: "10:00 AM – 12:00 PM", notes: "", passenger_count: "" });
      setPassengers([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (b) => {
    setExpanded(expanded === b.code ? null : b.code);
    setEditPassengers(b.passengers.map((p) => ({ ...p, age: String(p.age) })));
    setEditCount(String(b.passenger_count ?? b.passengers.length));
  };

  const saveEdit = async (code) => {
    const count = parseInt(editCount, 10);
    if (isNaN(count) || count < 1 || count > 50) return toast.error("Enter the number of passengers (1–50)");
    if (editPassengers.length > count) return toast.error("The passenger list has more names than the passenger count");
    const err = validatePassengers(editPassengers);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      await partnerApi().put(`/partner/bookings/${code}`, {
        passenger_count: count,
        passengers: editPassengers.map((p) => ({ name: p.name.trim(), age: parseInt(p.age, 10) })),
      });
      toast.success("Booking updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (code) => {
    if (!window.confirm("Cancel this charter? The date will reopen to the public.")) return;
    setBusy(true);
    try {
      await partnerApi().delete(`/partner/bookings/${code}`);
      toast.success("Charter cancelled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (authed === null) return <div className="min-h-screen pt-32 text-center text-slate-500">Loading…</div>;
  if (!authed) return <PartnerLogin onLogin={load} />;

  return (
    <div className="min-h-screen bg-slate-100 pt-24 pb-20 px-4 sm:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Partner Portal</h1>
            <p className="text-sm text-slate-500 mt-1">Book private Beach Getaway charters — booked dates are automatically blocked from public sale.</p>
          </div>
          <button data-testid="partner-logout-button" onClick={() => { localStorage.removeItem("mmc_partner_token"); setAuthed(false); }}
            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-red-500 transition-colors duration-150">
            <LogOut size={16} /> Sign out
          </button>
        </div>

        <div data-testid="partner-booking-form" className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 sm:p-8 mb-10">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-5">Book a charter date</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Date</label>
              <input data-testid="partner-date-input" type="date" className={`w-full ${inputCls}`} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5"># of Passengers</label>
              <input data-testid="partner-passenger-count-input" type="number" min="1" max="50" className={`w-full ${inputCls}`} placeholder="e.g. 30" value={form.passenger_count} onChange={(e) => setForm({ ...form, passenger_count: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Time</label>
              <input data-testid="partner-time-input" className={`w-full ${inputCls}`} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Notes (ship / excursion #)</label>
              <input data-testid="partner-notes-input" className={`w-full ${inputCls}`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-700 mb-1">Passenger list ({passengers.length}) — optional</h3>
          <p className="text-xs text-slate-500 mb-3">Names can be provided now or added later by editing the booking.</p>
          <PassengerEditor passengers={passengers} setPassengers={setPassengers} testPrefix="partner-new" />
          <div className="mt-6 flex justify-end">
            <button data-testid="partner-submit-booking-button" onClick={submit} disabled={busy}
              className="btn-coral text-white font-bold px-8 py-3.5 rounded-full disabled:opacity-60">
              {busy ? "Booking…" : "Reserve Charter"}
            </button>
          </div>
        </div>

        <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Your charters</h2>
        <div className="space-y-4">
          {bookings.length === 0 && <p className="text-sm text-slate-400 bg-white rounded-2xl p-6 border border-slate-200">No charters booked yet.</p>}
          {bookings.map((b) => (
            <div key={b.code} data-testid={`partner-booking-card-${b.code}`} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono-code font-bold text-sky-600">{b.code}</p>
                  <p className="text-sm text-slate-700 font-semibold mt-0.5">{prettyDate(b.date)} · {b.time}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{b.passenger_count ?? b.passengers.length} passengers{b.passengers.length === 0 ? " (names not provided)" : ""}{b.notes ? ` · ${b.notes}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${b.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {b.status.toUpperCase()}
                  </span>
                  {b.status === "confirmed" && (
                    <>
                      <button data-testid={`partner-edit-button-${b.code}`} onClick={() => openEdit(b)} className="text-sm font-bold text-sky-600 hover:text-sky-700">
                        {expanded === b.code ? "Close" : "Edit guests"}
                      </button>
                      <button data-testid={`partner-cancel-button-${b.code}`} onClick={() => cancel(b.code)} className="text-sm font-bold text-red-500 hover:text-red-600 flex items-center gap-1">
                        <XCircle size={14} /> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
              {expanded === b.code && b.status === "confirmed" && (
                <div className="mt-5 border-t border-slate-100 pt-5">
                  <div className="mb-4 max-w-[200px]">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5"># of Passengers</label>
                    <input data-testid={`partner-edit-count-${b.code}`} type="number" min="1" max="50" className={`w-full ${inputCls}`} value={editCount} onChange={(e) => setEditCount(e.target.value)} />
                  </div>
                  <p className="text-xs text-slate-500 mb-3">Passenger names (optional):</p>
                  <PassengerEditor passengers={editPassengers} setPassengers={setEditPassengers} testPrefix={`partner-edit-${b.code}`} />
                  <div className="mt-4">
                    <button data-testid={`partner-save-edit-button-${b.code}`} onClick={() => saveEdit(b.code)} disabled={busy}
                      className="btn-ocean text-white font-bold px-6 py-2.5 rounded-full text-sm disabled:opacity-60">
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
